import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { UserContext } from "../../../context/UserContext";
import apiFetch from "../../../context/api";
import { formatActivityDetails } from "./activityDetails";
import ActivityDetailModal from "./ActivityDetailModal";

const LIMIT = 50;

// Acción canónica (backend) → etiqueta legible + tono de color.
const ACTION_META = {
  "stock.entry":     { label: "Entrada de stock",  tone: "emerald" },
  "stock.exit":      { label: "Salida de stock",   tone: "rose" },
  "stock.adjust":    { label: "Ajuste de stock",   tone: "amber" },
  "stock.set_total": { label: "Total de stock",    tone: "amber" },
  "stock.transfer":  { label: "Traspaso de stock", tone: "cyan" },
  "sale.create":     { label: "Venta",             tone: "lime" },
  "socio.create":    { label: "Alta de socio",     tone: "emerald" },
  "socio.update":    { label: "Edición de socio",  tone: "slate" },
  "staff.create":    { label: "Alta de personal",  tone: "cyan" },
  "owner.create":    { label: "Alta de propietario", tone: "violet" },
};

const TONE_CLASSES = {
  emerald: "bg-emerald-100 text-emerald-800",
  rose:    "bg-rose-100 text-rose-800",
  amber:   "bg-amber-100 text-amber-800",
  cyan:    "bg-cyan-100 text-cyan-800",
  lime:    "bg-lime-100 text-lime-800",
  slate:   "bg-slate-100 text-slate-700",
  violet:  "bg-violet-100 text-violet-800",
};

function ActionBadge({ action }) {
  const meta = ACTION_META[action] || { label: action, tone: "slate" };
  return (
    <span className={`inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${TONE_CLASSES[meta.tone] || TONE_CLASSES.slate}`}>
      {meta.label}
    </span>
  );
}

// Resumen de una línea, para que la columna diga algo sin obligar a abrir nada.
// El detalle de verdad (las líneas de la venta, los movimientos del producto) vive en
// el modal: aquí solo caben los pares que el summary no repite ya.
function DetailPairs({ pairs }) {
  if (!pairs.length) return null;
  return (
    <div className="mt-0.5 text-xs text-slate-500">
      {pairs.map((p, i) => (
        <span key={p.label} className="whitespace-nowrap">
          {i > 0 && <span className="mx-1.5 text-slate-300">·</span>}
          <span className="text-slate-400">{p.label}:</span> {p.value}
        </span>
      ))}
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function ActivityPage() {
  const [token, , , venueID, venueName, , , accessPrivileges] = useContext(UserContext);

  // Espejo del filtro por rol del backend (activity_services.visible_actions_for):
  // el staff solo ve la operativa diaria, así que su desplegable no debe ofrecer
  // "Alta de personal/propietario" (el servidor las filtraría igualmente).
  const visibleActionMeta = useMemo(() => {
    if (accessPrivileges === "staff") {
      const teamActions = new Set(["staff.create", "owner.create"]);
      return Object.fromEntries(
        Object.entries(ACTION_META).filter(([key]) => !teamActions.has(key))
      );
    }
    return ACTION_META;
  }, [accessPrivileges]);

  const [items, setItems] = useState([]);
  // id → nombre de ubicación. `null` = no disponible (ver el efecto de carga).
  const [locations, setLocations] = useState(null);
  // Fila abierta en el modal de detalle (drill-down).
  const [detailItem, setDetailItem] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filtros
  const [actionFilter, setActionFilter] = useState("");
  const [since, setSince] = useState("");
  const [q, setQ] = useState("");

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async (nextOffset) => {
    if (!token || !venueID) { setLoading(false); return; }
    setLoading(true);
    setError("");
    const params = new URLSearchParams({ limit: String(LIMIT), offset: String(nextOffset) });
    if (actionFilter) params.set("action", actionFilter);
    if (since) {
      const d = new Date(since);
      if (!isNaN(d)) params.set("since", d.toISOString());
    }
    try {
      const res = await apiFetch(`/v1/venues/${venueID}/activity?${params.toString()}`, { headers: authHeaders });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      const newItems = data.items || [];
      setItems((prev) => (nextOffset === 0 ? newItems : [...prev, ...newItems]));
      setHasMore(Boolean(data.has_more));
      setOffset(nextOffset + newItems.length);
    } catch (e) {
      setError(e.message || "Error cargando la actividad");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, token, venueID, actionFilter, since]);

  // Recarga desde cero cuando cambian los filtros de servidor (acción/fecha).
  useEffect(() => {
    setItems([]);
    setOffset(0);
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, venueID, actionFilter, since]);

  // El log guarda las ubicaciones como UUID, así que hace falta el mapa id → nombre.
  // `/v1/stock/locations` exige permiso de edición de productos, pero la Actividad la
  // ve cualquier STAFF: un 403 aquí es esperable y NO debe romper la página — se queda
  // sin nombres de ubicación y el resto del detalle se muestra igual.
  useEffect(() => {
    if (!token || !venueID) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`/v1/stock/locations?venue_id=${venueID}`, { headers: authHeaders });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const byId = {};
        for (const loc of data || []) byId[loc.id] = loc.name;
        setLocations(byId);
      } catch {
        /* sin ubicaciones: se omiten, no se pinta el UUID crudo */
      }
    })();
    return () => { cancelled = true; };
  }, [token, venueID, authHeaders]);

  // El detalle legible se calcula una vez por fila: lo pinta la tabla y también lo
  // busca el filtro de texto (el placeholder ya prometía "detalle").
  const rows = useMemo(
    () => items.map((it) => ({ it, pairs: formatActivityDetails(it.action, it.details, locations) })),
    [items, locations]
  );

  // Búsqueda de texto: filtro en cliente sobre lo ya cargado.
  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter(({ it, pairs }) =>
      [it.summary, it.actor_email, it.action, ...pairs.map((p) => `${p.label} ${p.value}`)]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(needle))
    );
  }, [rows, q]);

  return (
    <div className="flex flex-col gap-4 p-0 sm:p-2 md:p-4">
      {/* Hero header */}
      <div className="overflow-hidden rounded-[28px] bg-[radial-gradient(circle_at_top_left,_rgba(132,204,22,0.22),_transparent_40%),linear-gradient(135deg,_#0f172a,_#14532d_55%,_#ecfccb)] p-6 text-white shadow-lg">
        <div className="text-xs font-semibold uppercase tracking-[0.28em] text-lime-200">Auditoría</div>
        <h1 className="mt-2 text-2xl font-bold md:text-3xl">Actividad</h1>
        <p className="mt-1 text-sm text-lime-50 opacity-80">
          Quién hizo qué, sobre qué y cuándo — stock, ventas, socios y personal.
          {venueName ? ` · ${venueName}` : ""}
        </p>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3 rounded-2xl bg-white p-4 shadow-sm">
        <label className="flex flex-col text-xs font-medium text-slate-600">
          Acción
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
          >
            <option value="">Todas</option>
            {Object.entries(visibleActionMeta).map(([key, meta]) => (
              <option key={key} value={key}>{meta.label}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-xs font-medium text-slate-600">
          Desde
          <input
            type="date"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
          />
        </label>
        <label className="flex flex-1 flex-col text-xs font-medium text-slate-600">
          Buscar
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Producto, usuario, detalle…"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800"
          />
        </label>
        {(actionFilter || since || q) && (
          <button
            onClick={() => { setActionFilter(""); setSince(""); setQ(""); }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Limpiar
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
      )}

      {/* Tabla */}
      <div className="overflow-x-auto rounded-2xl bg-white shadow-sm">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="whitespace-nowrap px-4 py-3">Fecha</th>
              <th className="whitespace-nowrap px-4 py-3">Usuario</th>
              <th className="whitespace-nowrap px-4 py-3">Acción</th>
              <th className="px-4 py-3">Detalle</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visible.map(({ it, pairs }) => (
              <tr
                key={it.id}
                onClick={() => setDetailItem(it)}
                // Sin esto el drill-down solo existe para quien use ratón.
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setDetailItem(it);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Ver detalle: ${it.summary || it.action}`}
                className="cursor-pointer align-top hover:bg-lime-50 focus:bg-lime-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-lime-500"
                title="Ver detalle"
              >
                <td className="whitespace-nowrap px-4 py-3 text-slate-600">{formatWhen(it.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-800">{it.actor_email || "—"}</div>
                  {it.actor_role && <div className="text-xs text-slate-400">{it.actor_role}</div>}
                </td>
                <td className="px-4 py-3"><ActionBadge action={it.action} /></td>
                <td className="px-4 py-3 text-slate-700">
                  <div>{it.summary || "—"}</div>
                  <DetailPairs pairs={pairs} />
                </td>
              </tr>
            ))}
            {!loading && visible.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center text-slate-400">
                  {q ? "Nada coincide con la búsqueda." : "Todavía no hay actividad registrada."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {detailItem && (
        <ActivityDetailModal
          item={detailItem}
          token={token}
          venueID={venueID}
          locations={locations}
          onClose={() => setDetailItem(null)}
        />
      )}

      {/* Paginación */}
      <div className="flex items-center justify-center gap-3">
        {loading && <span className="text-sm text-slate-400">Cargando…</span>}
        {!loading && hasMore && (
          <button
            onClick={() => load(offset)}
            className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700"
          >
            Cargar más
          </button>
        )}
      </div>
    </div>
  );
}
